/**
 * SecureBlob Tool Definitions — OpenAI function-calling format
 *
 * Defines the tools that agents can use to interact with the workspace.
 * Shared interface allows future MCP tools to use the same shape.
 */
import type { Workspace } from '../../types'
import { collectAgentSecretValues, type AgentConfig, type FileModification } from '../../agentTypes'
import {
    createComponentJobsClientOptions,
    createComponentRunnerBootstrap,
    createComponentSourceSnapshot,
} from '../../../components/componentJobs/apiClient'
import {
    prepareBuild,
    getBuildStatus,
    runServer,
    getRunStatus,
    downloadRunLog,
    RealWorkspaceSession,
    type SqliteConnection,
} from '@secureblob/sdk'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface ToolDefinition {
    type: 'function'
    function: {
        name: string
        description: string
        parameters: Record<string, any>
    }
}

export interface ToolResult {
    result: string
    filesModified: FileModification[]
    isError?: boolean
}

export interface SecureBlobToolSetting {
    name: string
    title: string
    description: string
    mode: 'read' | 'write'
}

export const SECUREBLOB_TOOL_SETTINGS: SecureBlobToolSetting[] = [
    {
        name: 'secureblob_list_files',
        title: 'List files',
        description: 'Discover the workspace structure before reading or editing.',
        mode: 'read',
    },
    {
        name: 'secureblob_read_text',
        title: 'Read text',
        description: 'Read full files or targeted line ranges from the workspace.',
        mode: 'read',
    },
    {
        name: 'secureblob_search_text',
        title: 'Search text',
        description: 'Search across workspace files for a text query.',
        mode: 'read',
    },
    {
        name: 'secureblob_sqlite_list',
        title: 'List SQLite DBs',
        description: 'Find SQLite database files in the workspace before opening one.',
        mode: 'read',
    },
    {
        name: 'secureblob_sqlite_schema',
        title: 'Inspect SQLite schema',
        description: 'Read tables, views, columns, indexes, and foreign keys from a SQLite database.',
        mode: 'read',
    },
    {
        name: 'secureblob_sqlite_query',
        title: 'Query SQLite',
        description: 'Run a single read-only SELECT query against a SQLite database.',
        mode: 'read',
    },
    {
        name: 'secureblob_stat',
        title: 'Inspect path',
        description: 'Get metadata for a file or directory and its latest related commit when available.',
        mode: 'read',
    },
    {
        name: 'secureblob_commit_log',
        title: 'Commit log',
        description: 'Review recent workspace history and affected files.',
        mode: 'read',
    },
    {
        name: 'secureblob_read_text_at_commit',
        title: 'Read past text',
        description: 'Read a file as it existed at a specific commit.',
        mode: 'read',
    },
    {
        name: 'secureblob_write_text',
        title: 'Write text',
        description: 'Create or fully overwrite workspace files.',
        mode: 'write',
    },
    {
        name: 'secureblob_patch_text',
        title: 'Patch text',
        description: 'Apply targeted edits inside existing files.',
        mode: 'write',
    },
    {
        name: 'secureblob_rename_path',
        title: 'Rename path',
        description: 'Rename or move a file within the workspace.',
        mode: 'write',
    },
    {
        name: 'secureblob_delete_file',
        title: 'Delete file',
        description: 'Delete files from the workspace.',
        mode: 'write',
    },
    {
        name: 'secureblob_component_build',
        title: 'Component job build',
        description: 'Start a component job build.',
        mode: 'write',
    },
    {
        name: 'secureblob_component_run',
        title: 'Component job run',
        description: 'Start a component job run from a completed build artifact.',
        mode: 'write',
    },
    {
        name: 'secureblob_component_job_status',
        title: 'Component job status',
        description: 'Poll build or run status.',
        mode: 'read',
    },
    {
        name: 'secureblob_component_log',
        title: 'Component job log',
        description: 'Download stdout/stderr logs from a completed run.',
        mode: 'read',
    },
]

function getSecureBlobToolOverride(
    config: AgentConfig,
    toolName: string,
): boolean | undefined {
    const toolConfig = config.tools?.secureBlob?.tools?.[toolName]
    if (typeof toolConfig === 'boolean') return toolConfig
    if (toolConfig && typeof toolConfig === 'object') return toolConfig.enabled !== false
    return undefined
}

export function isSecureBlobToolEnabled(config: AgentConfig, toolName: string): boolean {
    const secureBlobConfig = config.tools?.secureBlob
    if (secureBlobConfig?.enabled === false) return false

    const override = getSecureBlobToolOverride(config, toolName)
    if (override !== undefined) return override

    const toolMode = SECUREBLOB_TOOL_SETTINGS.find((tool) => tool.name === toolName)?.mode
    if (toolMode === 'write') return secureBlobConfig?.write !== false
    if (toolMode === 'read') return secureBlobConfig?.read !== false
    return true
}

function getComponentJobsToolEntry(workspace: Workspace): {
    apiBaseUrl: string
    capability: string
    blobId: string
} | null {
    const ws = workspace as any
    const entry = ws._entry ?? ws.deps?.entry
    if (!entry?.apiBaseUrl || !entry?.capability || !entry?.blobId) {
        return null
    }

    return entry
}

function normalizeComponentJobsToolEngine(value: unknown): 'auto' | 'wasmtime' | undefined {
    if (value == null) {
        return undefined
    }

    switch (value) {
        case 'auto':
        case 'wasmtime':
            return value as 'auto' | 'wasmtime'
        default:
            throw new Error('Engine must be "auto" or "wasmtime".')
    }
}

// ────────────────────────────────────────────────────────────────
// SecureBlob Tool Definitions
// ────────────────────────────────────────────────────────────────

export const SECUREBLOB_TOOLS: ToolDefinition[] = [
    {
        type: 'function',
        function: {
            name: 'secureblob_list_files',
            description: 'List files and directories in the workspace. Use this FIRST to discover the file structure before reading or writing files. Supports direct-child or recursive listing.',
            parameters: {
                type: 'object',
                properties: {
                    dirPath: { type: 'string', description: 'Directory to list. Defaults to "/" (root). Use nested paths like "/src" to narrow results.' },
                    recursive: { type: 'boolean', description: 'When true, include all descendants under dirPath. Defaults to false.' },
                    maxEntries: { type: 'integer', description: 'Maximum number of entries to return. Defaults to 200.' },
                }
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_read_text',
            description: 'Read a text file fully or by line range. Use this to understand existing code/content before making changes.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to the file, e.g. "/src/utils.ts"' },
                    startLine: { type: 'integer', description: 'Optional 1-based first line to return.' },
                    endLine: { type: 'integer', description: 'Optional 1-based last line to return, inclusive.' },
                    maxChars: { type: 'integer', description: 'Optional character cap for the returned text slice.' },
                },
                required: ['path']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_write_text',
            description: 'Create or completely overwrite a text file. For small targeted edits, prefer secureblob_patch_text instead. Use this for new files or full rewrites.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to write to, e.g. "/notes/todo.md"' },
                    content: { type: 'string', description: 'The complete file content to write' }
                },
                required: ['path', 'content']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_search_text',
            description: 'Search for a text pattern across all workspace files. Returns matching file paths and line content. Use this to find where something is defined or used before editing.',
            parameters: {
                type: 'object',
                properties: {
                    query: { type: 'string', description: 'Text or substring to search for' },
                    filePattern: { type: 'string', description: 'Optional glob pattern to filter files, e.g. "*.md" or "*.ts". Defaults to all files.' },
                    caseSensitive: { type: 'boolean', description: 'When true, preserve letter case. Defaults to false.' },
                    maxMatches: { type: 'integer', description: 'Maximum matches to return. Defaults to 50.' },
                },
                required: ['query']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_sqlite_list',
            description: 'List SQLite database files in the workspace by recognized database extension (.db, .sqlite, .sqlite3, .s3db, .sdb). Use this before schema or query tools when you do not know the database path yet.',
            parameters: {
                type: 'object',
                properties: {
                    dirPath: { type: 'string', description: 'Directory to scan. Defaults to "/" (root).' },
                    recursive: { type: 'boolean', description: 'When true, include nested directories under dirPath. Defaults to true.' },
                    maxEntries: { type: 'integer', description: 'Maximum database entries to return. Defaults to 100.' },
                },
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_sqlite_schema',
            description: 'Inspect a SQLite database schema. Returns tables, views, columns, indexes, and foreign keys for an existing database file in the workspace.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to the SQLite database file, e.g. "/data/app.db"' },
                    includeRowCounts: { type: 'boolean', description: 'When true, count rows per table. Defaults to false because counts can be expensive on large tables.' },
                },
                required: ['path'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_sqlite_query',
            description: 'Execute a single read-only SQLite query against an existing database file. Only SELECT/WITH/EXPLAIN SELECT statements are allowed. Results are capped to keep context manageable.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to the SQLite database file, e.g. "/data/app.db"' },
                    sql: { type: 'string', description: 'A single read-only SELECT query. Mutating SQL is rejected.' },
                    maxRows: { type: 'integer', description: 'Maximum rows to return to the agent. Defaults to 50 and is capped at 200.' },
                    timeoutMs: { type: 'integer', description: 'Query timeout in milliseconds. Defaults to 5000 and is capped at 30000.' },
                },
                required: ['path', 'sql'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_stat',
            description: 'Inspect a file or directory. Returns metadata such as kind, size, timestamps, and the latest related commit when history is available.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to inspect' },
                },
                required: ['path'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_commit_log',
            description: 'Read recent workspace history. Useful before edits when you need quick context on what changed and which files were touched.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Optional path filter. Only commits touching this path are returned.' },
                    maxCommits: { type: 'integer', description: 'Maximum commits to return. Defaults to 10.' },
                },
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_read_text_at_commit',
            description: 'Read a text file as it existed at a specific commit. Useful for quick history inspection without opening the full diff UI.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to the file' },
                    commitHash: { type: 'string', description: 'Commit hash from secureblob_commit_log' },
                    startLine: { type: 'integer', description: 'Optional 1-based first line to return.' },
                    endLine: { type: 'integer', description: 'Optional 1-based last line to return, inclusive.' },
                    maxChars: { type: 'integer', description: 'Optional character cap for the returned text slice.' },
                },
                required: ['path', 'commitHash'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_patch_text',
            description: 'Make a targeted find-and-replace edit within a file. PREFERRED over write_text for surgical edits — preserves surrounding content. The search string must match exactly (including whitespace). If the search string appears multiple times, the edit is rejected — narrow your search string to be unique.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'File path to patch' },
                    search: { type: 'string', description: 'Exact text to find (must be unique in the file)' },
                    replace: { type: 'string', description: 'Replacement text' }
                },
                required: ['path', 'search', 'replace']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_rename_path',
            description: 'Rename or move a file within the workspace. Use this instead of delete+write when the content should stay the same.',
            parameters: {
                type: 'object',
                properties: {
                    fromPath: { type: 'string', description: 'Current absolute path' },
                    toPath: { type: 'string', description: 'New absolute path' },
                },
                required: ['fromPath', 'toPath'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_delete_file',
            description: 'Delete a file from the workspace. This is permanent within the current session. Use with care — always confirm the path is correct.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Absolute path to the file to delete' }
                },
                required: ['path']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_component_build',
            description: 'Start a component job build. Returns buildId and initial stage. Poll secureblob_component_job_status for completion.',
            parameters: {
                type: 'object',
                properties: {
                    sourcePath: { type: 'string', description: 'Absolute path to the component job entrypoint (e.g. "/jobs/my-job.ts")' },
                    entrypoints: { type: 'array', items: { type: 'string' }, description: 'List of entrypoint paths to build' },
                    workspaceBlobId: { type: 'string', description: 'Workspace blob ID to build in' },
                },
                required: ['sourcePath', 'entrypoints', 'workspaceBlobId']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_component_run',
            description: 'Start a component job run from a completed build artifact.',
            parameters: {
                type: 'object',
                properties: {
                    artifactId: { type: 'string', description: 'Artifact ID from a completed build' },
                    workspaceBlobId: { type: 'string', description: 'Workspace blob ID to run in' },
                    engine: { type: 'string', description: 'Engine to run on: "wasmtime" or "auto" (default)' },
                    sourcePath: { type: 'string', description: 'Optional source path override for the run' },
                },
                required: ['artifactId', 'workspaceBlobId']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_component_job_status',
            description: 'Poll build or run status. Use buildId- prefix for build status, runId- prefix for run status.',
            parameters: {
                type: 'object',
                properties: {
                    buildId: { type: 'string', description: 'Build ID to query (starts with "build-")' },
                    runId: { type: 'string', description: 'Run ID to query (starts with "run-")' },
                },
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'secureblob_component_log',
            description: 'Download stdout/stderr logs from a completed run.',
            parameters: {
                type: 'object',
                properties: {
                    runId: { type: 'string', description: 'Run ID (starts with "run-")' },
                    logName: { type: 'string', description: 'Log file name: "stdout" or "stderr"' },
                },
                required: ['runId', 'logName']
            }
        }
    },
]

// ────────────────────────────────────────────────────────────────
// Tool Execution
// ────────────────────────────────────────────────────────────────

/** Redact sensitive values from text before sending to agent */
function redactTextForAgent(text: string, config: AgentConfig): string {
    let output = text
    for (const secret of collectAgentSecretValues(config)) {
        output = output.split(secret).join('***REDACTED***')
    }
    return output
}

function clampInteger(value: unknown, min: number, max: number, fallback: number): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
    return Math.min(max, Math.max(min, Math.round(value)))
}

function normalizeDirectoryPath(value: unknown): string {
    const raw = typeof value === 'string' && value.trim() ? value.trim() : '/'
    if (raw === '/') return '/'
    const normalized = raw.startsWith('/') ? raw : `/${raw}`
    return normalized.replace(/\/+$/, '') || '/'
}

function isDirectChildPath(path: string, dirPath: string): boolean {
    if (dirPath === '/') {
        const relative = path.replace(/^\/+/, '')
        return relative.length > 0 && !relative.includes('/')
    }
    if (!path.startsWith(`${dirPath}/`)) return false
    const relative = path.slice(dirPath.length + 1)
    return relative.length > 0 && !relative.includes('/')
}

function sliceTextContent(
    content: string,
    args: Record<string, any>,
): { text?: string; error?: string } {
    const hasLineSelection = args.startLine != null || args.endLine != null
    const hasCharCap = args.maxChars != null

    if (!hasLineSelection && !hasCharCap) {
        return { text: content }
    }

    const lines = content.split('\n')
    const startLine = args.startLine == null
        ? 1
        : clampInteger(args.startLine, 1, Math.max(1, lines.length), 1)
    const endLine = args.endLine == null
        ? lines.length
        : clampInteger(args.endLine, startLine, Math.max(startLine, lines.length), lines.length)

    if (startLine > endLine) {
        return { error: `Error: Invalid line range ${startLine}-${endLine}` }
    }

    let text = lines.slice(startLine - 1, endLine).join('\n')
    if (args.maxChars != null) {
        const maxChars = clampInteger(args.maxChars, 1, 100_000, 4_000)
        text = text.slice(0, maxChars)
    }

    return { text }
}

function getHistoryApi(workspace: Workspace): any {
    return (workspace as any).history
}

function getLatestCommitForPath(workspace: Workspace, path: string): Record<string, unknown> | null {
    const history = getHistoryApi(workspace)
    const commits = history?.list?.()
    if (!Array.isArray(commits)) return null

    const commit = commits.find((entry: any) =>
        Array.isArray(entry?.affectedFiles) && entry.affectedFiles.some((file: any) => file?.path === path),
    )
    if (!commit) return null

    return {
        commitHash: commit.commitHash,
        time: commit.time,
        writerName: commit.writerName,
        writerKid: commit.writerKid,
    }
}

const SQLITE_TOOL_SESSION_CACHE = new WeakMap<Workspace, RealWorkspaceSession>()
const SQLITE_FILE_EXTENSIONS = ['.db', '.sqlite', '.sqlite3', '.s3db', '.sdb'] as const
const DEFAULT_SQLITE_LIST_MAX_ENTRIES = 100
const MAX_SQLITE_LIST_MAX_ENTRIES = 1_000
const DEFAULT_SQLITE_QUERY_MAX_ROWS = 50
const MAX_SQLITE_QUERY_MAX_ROWS = 200
const DEFAULT_SQLITE_QUERY_TIMEOUT_MS = 5_000
const MAX_SQLITE_QUERY_TIMEOUT_MS = 30_000
const SQLITE_MUTATING_KEYWORDS = /\b(?:insert|update|delete|replace|create|alter|drop|truncate|attach|detach|vacuum|reindex|analyze|pragma|begin|commit|rollback|savepoint|release)\b/i

function getSqliteToolSession(workspace: Workspace): RealWorkspaceSession {
    let session = SQLITE_TOOL_SESSION_CACHE.get(workspace)
    if (!session) {
        session = new RealWorkspaceSession(workspace)
        SQLITE_TOOL_SESSION_CACHE.set(workspace, session)
    }
    return session
}

function isSqliteDatabasePath(path: string): boolean {
    const normalized = path.trim().toLowerCase()
    return SQLITE_FILE_EXTENSIONS.some(ext => normalized.endsWith(ext))
}

function normalizeSqliteDatabasePath(value: unknown): string {
    const raw = typeof value === 'string' ? value.trim() : ''
    if (!raw) return ''
    return raw.startsWith('/') ? raw : `/${raw}`
}

function ensureSqliteDatabaseFile(workspace: Workspace, path: string): void {
    if (!path) {
        throw new Error('SQLite database path is required.')
    }
    const stat = workspace.fs.stat(path)
    if (!stat || stat.kind !== 'file') {
        throw new Error(`SQLite database not found: ${path}`)
    }
}

async function withSqliteConnection<T>(
    workspace: Workspace,
    path: string,
    label: string,
    fn: (conn: SqliteConnection) => Promise<T>,
): Promise<T> {
    ensureSqliteDatabaseFile(workspace, path)
    const session = getSqliteToolSession(workspace)
    const db = await session.openSqlite(path)
    const conn = await db.connect({ label })
    try {
        return await fn(conn)
    } finally {
        try {
            await conn.close()
        } catch {
            // Best-effort connection cleanup; the cached database stays owned by the session.
        }
    }
}

function sanitizeSqlForValidation(sql: string): string {
    let output = ''
    let index = 0

    while (index < sql.length) {
        const char = sql[index] ?? ''
        const next = sql[index + 1] ?? ''

        if (char === '-' && next === '-') {
            index += 2
            while (index < sql.length && sql[index] !== '\n') index++
            output += ' '
            continue
        }

        if (char === '/' && next === '*') {
            index += 2
            while (index < sql.length && !(sql[index] === '*' && sql[index + 1] === '/')) index++
            index = Math.min(sql.length, index + 2)
            output += ' '
            continue
        }

        if (char === '\'') {
            output += ' '
            index++
            while (index < sql.length) {
                if (sql[index] === '\'' && sql[index + 1] === '\'') {
                    index += 2
                    continue
                }
                if (sql[index] === '\'') {
                    index++
                    break
                }
                index++
            }
            continue
        }

        if (char === '"' || char === '`') {
            output += ' '
            index++
            while (index < sql.length) {
                if (sql[index] === char && sql[index + 1] === char) {
                    index += 2
                    continue
                }
                if (sql[index] === char) {
                    index++
                    break
                }
                index++
            }
            continue
        }

        if (char === '[') {
            output += ' '
            index++
            while (index < sql.length && sql[index] !== ']') index++
            index = Math.min(sql.length, index + 1)
            continue
        }

        output += char
        index++
    }

    return output
}

function normalizeReadonlySqliteQuery(value: unknown): { sql?: string; error?: string } {
    const trimmed = typeof value === 'string' ? value.trim() : ''
    if (!trimmed) {
        return { error: 'Error: SQL query is required.' }
    }

    const singleStatement = trimmed.replace(/;+\s*$/, '').trim()
    if (!singleStatement) {
        return { error: 'Error: SQL query is required.' }
    }

    const sanitized = sanitizeSqlForValidation(singleStatement)
    if (sanitized.includes(';')) {
        return { error: 'Error: Only a single SQL statement is allowed.' }
    }

    const normalized = sanitized.trim().toLowerCase()
    if (!normalized) {
        return { error: 'Error: SQL query is required.' }
    }

    const explainMatch = normalized.match(/^explain(?:\s+query\s+plan)?\s+(.*)$/s)
    const candidate = explainMatch ? explainMatch[1]?.trimStart() ?? '' : normalized
    if (!/^(select|with)\b/.test(candidate)) {
        return { error: 'Error: secureblob_sqlite_query only allows read-only SELECT statements. Use secureblob_sqlite_schema for schema inspection.' }
    }

    if (SQLITE_MUTATING_KEYWORDS.test(candidate)) {
        return { error: 'Error: secureblob_sqlite_query rejects mutating or DDL SQL. Use a read-only SELECT or CTE query.' }
    }

    return { sql: singleStatement }
}

function normalizeSqliteValue(value: unknown): unknown {
    if (value == null) return null
    if (value instanceof Uint8Array) {
        return {
            $sqliteBlob: Array.from(value).map(byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase(),
        }
    }
    if (typeof value === 'bigint') return value.toString()
    if (value instanceof Date) return value.toISOString()
    if (Array.isArray(value)) return value.map(entry => normalizeSqliteValue(entry))
    if (typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, normalizeSqliteValue(entry)]),
        )
    }
    return value
}

function collectSqliteResultColumns(rows: Array<Record<string, unknown>>): string[] {
    const columns: string[] = []
    const seen = new Set<string>()
    for (const row of rows) {
        for (const column of Object.keys(row)) {
            if (!seen.has(column)) {
                seen.add(column)
                columns.push(column)
            }
        }
    }
    return columns
}

async function loadSqliteSchema(
    conn: SqliteConnection,
    includeRowCounts: boolean,
): Promise<Record<string, unknown>> {
    const masterRows = await conn.query<{ type: string; name: string; sql: string | null }>(
        `SELECT type, name, sql FROM sqlite_master WHERE type IN ('table', 'view') AND name NOT LIKE 'sqlite_%' ORDER BY name`,
    )

    const tables: Array<Record<string, unknown>> = []
    const views: Array<Record<string, unknown>> = []
    const foreignKeys: Record<string, unknown[]> = {}

    for (const row of masterRows) {
        const escapedName = row.name.replace(/'/g, "''")
        const quotedName = row.name.replace(/"/g, '""')
        const columns = await conn.query<{
            cid: number
            name: string
            type: string
            notnull: number
            dflt_value: string | null
            pk: number
        }>(`PRAGMA table_info('${escapedName}')`)

        let rowCount: number | undefined
        if (includeRowCounts && row.type === 'table') {
            const countRows = await conn.query<{ cnt: number }>(
                `SELECT COUNT(*) as cnt FROM "${quotedName}"`,
            )
            rowCount = countRows[0]?.cnt ?? 0
        }

        const fks = await conn.query<{
            id: number
            seq: number
            table: string
            from: string
            to: string
            on_update: string
            on_delete: string
        }>(`PRAGMA foreign_key_list('${escapedName}')`)
        if (fks.length > 0) {
            foreignKeys[row.name] = fks.map((fk) => ({
                id: fk.id,
                seq: fk.seq,
                table: fk.table,
                from: fk.from,
                to: fk.to,
                onUpdate: fk.on_update,
                onDelete: fk.on_delete,
            }))
        }

        const objectInfo: Record<string, unknown> = {
            name: row.name,
            type: row.type,
            sql: row.sql ?? '',
            columns: columns.map(column => ({
                cid: column.cid,
                name: column.name,
                type: column.type,
                notNull: column.notnull === 1,
                defaultValue: column.dflt_value,
                primaryKey: column.pk === 1,
            })),
        }
        if (includeRowCounts && row.type === 'table') {
            objectInfo.rowCount = rowCount ?? 0
        }

        if (row.type === 'view') {
            views.push(objectInfo)
        } else {
            tables.push(objectInfo)
        }
    }

    const indexes: Array<Record<string, unknown>> = []
    for (const table of tables) {
        const tableName = String(table.name ?? '')
        const escapedName = tableName.replace(/'/g, "''")
        const tableIndexes = await conn.query<{ name: string; unique: number }>(
            `PRAGMA index_list('${escapedName}')`,
        )
        for (const indexInfo of tableIndexes) {
            const columns = await conn.query<{ name: string }>(
                `PRAGMA index_info('${indexInfo.name.replace(/'/g, "''")}')`,
            )
            indexes.push({
                name: indexInfo.name,
                tableName,
                unique: indexInfo.unique === 1,
                columns: columns.map(column => column.name),
            })
        }
    }

    indexes.sort((left, right) => String(left.name).localeCompare(String(right.name)))

    return {
        tableCount: tables.length,
        viewCount: views.length,
        indexCount: indexes.length,
        includeRowCounts,
        tables,
        views,
        indexes,
        foreignKeys,
    }
}

/**
 * Execute a SecureBlob tool call and return the result.
 * This function is the single dispatch point for all built-in tools.
 */
export async function executeSecureBlobTool(
    name: string,
    args: Record<string, any>,
    workspace: Workspace,
    config: AgentConfig,
): Promise<ToolResult> {
    let result = ''
    const filesModified: FileModification[] = []
    let isError = false

    try {
        if (name === 'secureblob_list_files') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const dirPath = normalizeDirectoryPath(args.dirPath)
                const recursive = args.recursive === true
                const maxEntries = clampInteger(args.maxEntries, 1, 2_000, 200)
                const list = workspace.fs.list(dirPath)
                const filtered = recursive
                    ? list.filter(entry => entry.path === dirPath || entry.path.startsWith(dirPath === '/' ? '/' : `${dirPath}/`))
                    : list.filter(entry => isDirectChildPath(entry.path, dirPath))
                result = JSON.stringify(filtered.slice(0, maxEntries), null, 2)
            }

        } else if (name === 'secureblob_read_text') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const content = await workspace.fs.readText(args.path)
                if (content == null) {
                    result = 'File not found'
                } else {
                    const sliced = sliceTextContent(content, args)
                    if (sliced.error) {
                        result = sliced.error
                        isError = true
                    } else {
                        result = sliced.text ?? ''
                    }
                }
            }

        } else if (name === 'secureblob_write_text') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                workspace.fs.writeText(args.path, args.content)
                result = `Successfully wrote ${args.content.length} characters to ${args.path}`
                filesModified.push({ path: args.path, action: 'updated' })
            }

        } else if (name === 'secureblob_search_text') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const query = String(args.query ?? '')
                const caseSensitive = args.caseSensitive === true
                const normalizedQuery = caseSensitive ? query : query.toLowerCase()
                const pattern = args.filePattern as string | undefined
                const maxMatches = clampInteger(args.maxMatches, 1, 200, 50)
                const allFiles = workspace.fs.list()
                const textFiles = allFiles.filter(f =>
                    f.kind === 'file' &&
                    (!pattern || matchGlob(f.path, pattern))
                )

                const matches: { path: string; line: number; text: string }[] = []
                for (const file of textFiles) {
                    if (matches.length >= maxMatches) break
                    try {
                        const content = await workspace.fs.readText(file.path)
                        if (!content) continue
                        const lines = content.split('\n')
                        for (let i = 0; i < lines.length; i++) {
                            const line = caseSensitive ? lines[i] : lines[i].toLowerCase()
                            if (line.includes(normalizedQuery)) {
                                matches.push({ path: file.path, line: i + 1, text: lines[i].trim().slice(0, 120) })
                                if (matches.length >= maxMatches) break
                            }
                        }
                    } catch { /* skip unreadable files */ }
                }

                result = matches.length > 0
                    ? `Found ${matches.length} match(es):\n${matches.map(m => `${m.path}:${m.line}: ${m.text}`).join('\n')}`
                    : 'No matches found'
            }

        } else if (name === 'secureblob_sqlite_list') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const dirPath = normalizeDirectoryPath(args.dirPath)
                const recursive = args.recursive !== false
                const maxEntries = clampInteger(args.maxEntries, 1, MAX_SQLITE_LIST_MAX_ENTRIES, DEFAULT_SQLITE_LIST_MAX_ENTRIES)
                const list = workspace.fs.list(dirPath)
                const filtered = list
                    .filter(entry =>
                        entry.kind === 'file'
                        && isSqliteDatabasePath(entry.path)
                        && (recursive
                            ? entry.path === dirPath || entry.path.startsWith(dirPath === '/' ? '/' : `${dirPath}/`)
                            : isDirectChildPath(entry.path, dirPath)),
                    )
                    .sort((left, right) => left.path.localeCompare(right.path))
                result = JSON.stringify({
                    dirPath,
                    recursive,
                    databaseCount: filtered.length,
                    truncated: filtered.length > maxEntries,
                    databases: filtered.slice(0, maxEntries).map(entry => ({
                        path: entry.path,
                        size: entry.size,
                        mime: entry.mime,
                        mtime: entry.mtime,
                        ctime: entry.ctime,
                    })),
                }, null, 2)
            }

        } else if (name === 'secureblob_sqlite_schema') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const path = normalizeSqliteDatabasePath(args.path)
                const includeRowCounts = args.includeRowCounts === true
                try {
                    const schema = await withSqliteConnection(workspace, path, 'Agent schema', async (conn) => (
                        await loadSqliteSchema(conn, includeRowCounts)
                    ))
                    result = JSON.stringify({
                        databasePath: path,
                        ...schema,
                    }, null, 2)
                } catch (error) {
                    result = error instanceof Error ? `Error: ${error.message}` : `Error: ${String(error)}`
                    isError = true
                }
            }

        } else if (name === 'secureblob_sqlite_query') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const path = normalizeSqliteDatabasePath(args.path)
                const normalizedQuery = normalizeReadonlySqliteQuery(args.sql)
                if (normalizedQuery.error) {
                    result = normalizedQuery.error
                    isError = true
                } else {
                    const maxRows = clampInteger(args.maxRows, 1, MAX_SQLITE_QUERY_MAX_ROWS, DEFAULT_SQLITE_QUERY_MAX_ROWS)
                    const timeoutMs = clampInteger(args.timeoutMs, 100, MAX_SQLITE_QUERY_TIMEOUT_MS, DEFAULT_SQLITE_QUERY_TIMEOUT_MS)
                    try {
                        const queryResult = await withSqliteConnection(workspace, path, 'Agent query', async (conn) => {
                            const controller = new AbortController()
                            const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
                            try {
                                const rows = await conn.query<Record<string, unknown>>(
                                    normalizedQuery.sql ?? '',
                                    undefined,
                                    { signal: controller.signal },
                                )
                                const normalizedRows = rows.map((row) => normalizeSqliteValue(row) as Record<string, unknown>)
                                const limitedRows = normalizedRows.slice(0, maxRows)
                                return {
                                    sql: normalizedQuery.sql ?? '',
                                    rowCount: normalizedRows.length,
                                    returnedRows: limitedRows.length,
                                    truncated: normalizedRows.length > maxRows,
                                    maxRows,
                                    columns: collectSqliteResultColumns(limitedRows),
                                    rows: limitedRows,
                                }
                            } catch (error) {
                                if (controller.signal.aborted) {
                                    throw new Error(`SQLite query timed out after ${timeoutMs} ms.`)
                                }
                                throw new Error(error instanceof Error ? error.message : String(error))
                            } finally {
                                clearTimeout(timeoutId)
                            }
                        })
                        result = JSON.stringify({
                            databasePath: path,
                            timeoutMs,
                            ...queryResult,
                        }, null, 2)
                    } catch (error) {
                        result = error instanceof Error ? `Error: ${error.message}` : `Error: ${String(error)}`
                        isError = true
                    }
                }
            }

        } else if (name === 'secureblob_stat') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const stat = workspace.fs.stat(args.path)
                if (!stat) {
                    result = `Error: Path not found: ${args.path}`
                    isError = true
                } else {
                    result = JSON.stringify({
                        ...stat,
                        latestCommit: getLatestCommitForPath(workspace, args.path),
                    }, null, 2)
                }
            }

        } else if (name === 'secureblob_commit_log') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const history = getHistoryApi(workspace)
                const commits = history?.list?.()
                if (!Array.isArray(commits)) {
                    result = 'Error: Workspace history is unavailable'
                    isError = true
                } else {
                    const pathFilter = typeof args.path === 'string' && args.path.trim() ? args.path.trim() : ''
                    const maxCommits = clampInteger(args.maxCommits, 1, 30, 10)
                    const filtered = commits
                        .filter((commit: any) => !pathFilter || commit.affectedFiles?.some((file: any) => file?.path === pathFilter))
                        .slice(0, maxCommits)
                        .map((commit: any) => ({
                            commitHash: commit.commitHash,
                            time: commit.time,
                            writerName: commit.writerName,
                            writerKid: commit.writerKid,
                            affectedFiles: Array.isArray(commit.affectedFiles)
                                ? commit.affectedFiles.map((file: any) => ({
                                    path: file.path,
                                    changeType: file.changeType,
                                }))
                                : [],
                        }))
                    result = JSON.stringify(filtered, null, 2)
                }
            }

        } else if (name === 'secureblob_read_text_at_commit') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const history = getHistoryApi(workspace)
                if (typeof history?.readTextAtCommit !== 'function') {
                    result = 'Error: Workspace history is unavailable'
                    isError = true
                } else {
                    const content = await history.readTextAtCommit(args.path, args.commitHash)
                    if (content == null) {
                        result = `Error: No text found for ${args.path} at commit ${args.commitHash}`
                        isError = true
                    } else {
                        const sliced = sliceTextContent(content, args)
                        if (sliced.error) {
                            result = sliced.error
                            isError = true
                        } else {
                            result = sliced.text ?? ''
                        }
                    }
                }
            }

        } else if (name === 'secureblob_patch_text') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const content = await workspace.fs.readText(args.path)
                if (content == null) {
                    result = `Error: File not found: ${args.path}`
                    isError = true
                } else {
                    const search = args.search as string
                    const replace = args.replace as string

                    if (!search) {
                        result = 'Error: Search string must not be empty.\nRECOMMENDATION: Use secureblob_read_text to see the current file content, then provide a non-empty unique search string.'
                        isError = true
                    } else {
                        // Count occurrences
                        let count = 0
                        let idx = 0
                        while ((idx = content.indexOf(search, idx)) !== -1) {
                            count++
                            idx += search.length
                        }

                        if (count === 0) {
                            result = 'Error: Search string not found in file. Make sure it matches exactly (including whitespace and newlines).\nRECOMMENDATION: Use secureblob_read_text to see the current file content before retrying.'
                            isError = true
                        } else if (count > 1) {
                            result = `Error: Search string found ${count} times — it must be unique. Include more surrounding context to make it unambiguous.\nRECOMMENDATION: Use secureblob_read_text to find unique surrounding text.`
                            isError = true
                        } else {
                            const patched = content.replace(search, replace)
                            workspace.fs.writeText(args.path, patched)
                            result = `Successfully patched ${args.path} (replaced ${search.length} chars with ${replace.length} chars)`
                            filesModified.push({ path: args.path, action: 'updated' })
                        }
                    } // end empty-search guard
                }
            }

        } else if (name === 'secureblob_rename_path') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const fromPath = String(args.fromPath ?? '')
                const toPath = String(args.toPath ?? '')
                if (!fromPath || !toPath) {
                    result = 'Error: fromPath and toPath are required'
                    isError = true
                } else if (!workspace.fs.stat(fromPath)) {
                    result = `Error: Path not found: ${fromPath}`
                    isError = true
                } else {
                    const renamed = workspace.fs.rename?.(fromPath, toPath)
                    if (!renamed) {
                        result = `Error: Failed to rename ${fromPath} to ${toPath}`
                        isError = true
                    } else {
                        result = `Renamed ${fromPath} to ${toPath}`
                        filesModified.push({ path: fromPath, action: 'deleted' })
                        filesModified.push({ path: toPath, action: 'created' })
                    }
                }
            }

        } else if (name === 'secureblob_delete_file') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: tool is disabled by agent config'
                isError = true
            } else {
                const exists = workspace.fs.stat(args.path)
                if (!exists) {
                    result = `Error: File not found: ${args.path}`
                    isError = true
                } else {
                    workspace.fs.delete(args.path)
                    result = `Deleted ${args.path}`
                    filesModified.push({ path: args.path, action: 'deleted' })
                }
            }

        } else if (name === 'secureblob_component_build') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: secureblob_component_build is disabled by agent config'
                isError = true
            } else {
                try {
                    const entry = getComponentJobsToolEntry(workspace)
                    if (!entry) {
                        result = 'Error: Workspace does not expose API credentials. Component job tools require the active workspace to provide apiBaseUrl, capability, and blobId.'
                        isError = true
                    } else {
                        const sourcePath = args.sourcePath as string
                        const sourceStat = workspace.fs.stat(sourcePath)
                        if (!sourceStat) {
                            result = `Error: File not found: ${sourcePath}`
                            isError = true
                        } else if (sourceStat.kind !== 'file') {
                            result = `Error: Component build sourcePath must point to a file: ${sourcePath}`
                            isError = true
                        } else {
                            const status = await prepareBuild({
                                ...createComponentJobsClientOptions(entry),
                                sourceSnapshot: createComponentSourceSnapshot(
                                    args.workspaceBlobId as string,
                                    sourcePath,
                                    sourceStat.mtime,
                                ),
                                entrypoints: (args.entrypoints as string[]) ?? [sourcePath],
                                workspaceBlobId: args.workspaceBlobId as string,
                                runnerBootstrap: createComponentRunnerBootstrap(entry),
                            })
                            result = `Build started: ${status.buildId}\nStage: ${status.stage}\nMessage: ${status.message}\nStarted at: ${status.startedAt}`
                        }
                    }
                } catch (err) {
                    result = `Error starting build: ${err}`
                    isError = true
                }
            }

        } else if (name === 'secureblob_component_run') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: secureblob_component_run is disabled by agent config'
                isError = true
            } else {
                try {
                    const entry = getComponentJobsToolEntry(workspace)
                    if (!entry) {
                        result = 'Error: Workspace does not expose API credentials. Component job tools require the active workspace to provide apiBaseUrl, capability, and blobId.'
                        isError = true
                    } else {
                        const engine = normalizeComponentJobsToolEngine(args.engine)
                        const status = await runServer({
                            ...createComponentJobsClientOptions(entry),
                            artifactId: args.artifactId as string,
                            workspaceBlobId: args.workspaceBlobId as string,
                            runnerBootstrap: createComponentRunnerBootstrap(entry),
                            ...(engine ? { engine } : {}),
                            ...(args.sourcePath ? { sourcePath: args.sourcePath as string } : {}),
                        })
                        result = `Run started: ${status.runId}\nStage: ${status.stage}\nMessage: ${status.message}\nStarted at: ${status.startedAt}`
                    }
                } catch (err) {
                    result = `Error starting run: ${err}`
                    isError = true
                }
            }

        } else if (name === 'secureblob_component_job_status') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: secureblob_component_job_status is disabled by agent config'
                isError = true
            } else {
                try {
                    const entry = getComponentJobsToolEntry(workspace)
                    if (!entry) {
                        result = 'Error: Workspace does not expose API credentials. Component job tools require the active workspace to provide apiBaseUrl, capability, and blobId.'
                        isError = true
                    } else {
                        const clientOptions = createComponentJobsClientOptions(entry)
                        if (args.buildId) {
                            const status = await getBuildStatus({ ...clientOptions, buildId: args.buildId as string })
                            result = `Build ${status.buildId}: stage=${status.stage}, elapsed=${status.totalElapsedSeconds}s\n${status.message}`
                            if (status.error) result += `\nError: ${status.error}`
                            if (status.artifactId) result += `\nArtifact: ${status.artifactId}`
                        } else if (args.runId) {
                            const status = await getRunStatus({ ...clientOptions, runId: args.runId as string })
                            result = `Run ${status.runId}: stage=${status.stage}, engine=${status.engine}, elapsed=${status.totalElapsedSeconds}s\n${status.message}`
                            if (status.error) result += `\nError: ${status.error}`
                        } else {
                            result = 'Error: provide buildId or runId'
                            isError = true
                        }
                    }
                } catch (err) {
                    result = `Error querying status: ${err}`
                    isError = true
                }
            }

        } else if (name === 'secureblob_component_log') {
            if (!isSecureBlobToolEnabled(config, name)) {
                result = 'Error: secureblob_component_log is disabled by agent config'
                isError = true
            } else {
                try {
                    const entry = getComponentJobsToolEntry(workspace)
                    if (!entry) {
                        result = 'Error: Workspace does not expose API credentials. Component job tools require the active workspace to provide apiBaseUrl, capability, and blobId.'
                        isError = true
                    } else {
                        const log = await downloadRunLog({
                            ...createComponentJobsClientOptions(entry),
                            runId: args.runId as string,
                            logName: args.logName as string,
                        })
                        result = log
                    }
                } catch (err) {
                    result = `Error downloading log: ${err}`
                    isError = true
                }
            }

        } else {
            result = `Error: Unknown tool ${name}`
            isError = true
        }
    } catch (err) {
        result = `Error executing tool: ${err}`
        isError = true
    }

    result = redactTextForAgent(result, config)
    return { result, filesModified, isError }
}

/** Simple glob matcher (supports * wildcard) */
function matchGlob(path: string, pattern: string): boolean {
    const fileName = path.split('/').pop() ?? ''
    const regex = new RegExp(
        '^' + pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$',
        'i',
    )
    return regex.test(fileName)
}
